import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";

import {
  useGetAllCategories,
  useGetCategoriesWithConsentForms,
  useUploadCategoryConsentMutation,
} from "../../../../../api/jeep-rally/categories/categories.hooks";
import { useGetRallyInspections } from "../../../../../api/jeep-rally/inspections/inspections.hooks";
import {
  useGetActiveRally,
  useGetPreviousRallies,
  useGetRallies,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import {
  useGetRegistrationById,
  useGetRegistrationsByEvent,
  useUpdateCompetitorApprovalMutation,
  useUpdateFiringOrderMutation,
  useUpdateRallyNumberMutation,
} from "../../../../../api/jeep-rally/registrations/registrations.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import {
  normalizeRallies,
  normalizeRally,
} from "../../Rallies/utils/rally.utils";
import CompetitorEditModal from "./_components/CompetitorEditModal";
import CompetitorsFilters from "./_components/CompetitorsFilters";
import CompetitorsTable from "./_components/CompetitorsTable";
import InspectionModal from "./_components/InspectionModal";
import RegistrationDetailModal from "./_components/RegistrationDetailModal";
import TeamDetailModal from "./_components/TeamDetailModal";
import UploadConsentModal from "./_components/UploadConsentModal";
import {
  downloadConsentFormsForRegistrations,
  downloadPersonalizedConsent,
  getEntityId,
  getInheritedConsentInfo,
  getRegistrationApiErrorMessage,
  mergeRegistrationsWithInspections,
  normalizeActiveRally,
  normalizeInspections,
  normalizeRallyOptions,
  normalizeRegistration,
  normalizeRegistrations,
  openOrDownloadFile,
} from "./utils/competitors.utils";

const CompetitorsListPage = () => {
  const canManage = isJeepRallyAdmin();

  const [selectedRallyId, setSelectedRallyId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const [detailId, setDetailId] = useState("");
  const [detailFallback, setDetailFallback] = useState(null);
  const [detailMode, setDetailMode] = useState("view");

  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [approvingId, setApprovingId] = useState("");
  const [savingFiringOrderId, setSavingFiringOrderId] = useState("");
  const [savingRallyNumberId, setSavingRallyNumberId] = useState("");

  const [inspectionTarget, setInspectionTarget] = useState(null);
  const [teamTarget, setTeamTarget] = useState(null);

  const {
    data: activeRallyData,
    isLoading: isActiveRallyLoading,
    isError: isActiveRallyError,
    error: activeRallyError,
  } = useGetActiveRally();

  const activeRally = useMemo(
    () =>
      normalizeActiveRally(activeRallyData) || normalizeRally(activeRallyData),
    [activeRallyData]
  );
  const activeEventId = getEntityId(activeRally);

  const {
    data: previousRalliesData,
    isLoading: isPreviousLoading,
  } = useGetPreviousRallies();

  const {
    data: ralliesData,
    isLoading: isRalliesLoading,
    isError: isRalliesError,
    error: ralliesError,
  } = useGetRallies();

  const previousRallies = useMemo(
    () => normalizeRallies(previousRalliesData),
    [previousRalliesData]
  );
  const allRallies = useMemo(() => normalizeRallies(ralliesData), [ralliesData]);

  const rallyOptions = useMemo(
    () =>
      normalizeRallyOptions({
        activeRally,
        previousRallies,
        allRallies,
      }),
    [activeRally, previousRallies, allRallies]
  );

  useEffect(() => {
    if (selectedRallyId) return;
    if (activeEventId) {
      setSelectedRallyId(activeEventId);
    }
  }, [activeEventId, selectedRallyId]);

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const { data: signedCategoriesData } = useGetCategoriesWithConsentForms();

  const categories = useMemo(() => {
    const all = normalizeCategories(categoriesData);
    const signed = normalizeCategories(signedCategoriesData);
    if (!signed.length) return all;

    const byId = new Map();
    all.forEach((item) => {
      const id = item._id || item.id;
      if (id) byId.set(id, item);
    });
    signed.forEach((item) => {
      const id = item._id || item.id;
      if (!id) return;
      byId.set(id, { ...(byId.get(id) || {}), ...item });
    });
    return Array.from(byId.values());
  }, [categoriesData, signedCategoriesData]);

  const listParams = useMemo(() => {
    const params = {};
    if (categoryId) params.category_id = categoryId;
    if (status) params.status = status;
    return params;
  }, [categoryId, status]);

  const {
    data: registrationsData,
    isLoading: isRegistrationsLoading,
    isFetching: isRegistrationsFetching,
    isError: isRegistrationsError,
    error: registrationsError,
    refetch: refetchRegistrations,
  } = useGetRegistrationsByEvent(selectedRallyId, listParams, {
    enabled: Boolean(selectedRallyId),
  });

  const {
    data: inspectionsData,
    isLoading: isInspectionsLoading,
    refetch: refetchInspections,
  } = useGetRallyInspections(selectedRallyId, {
    enabled: Boolean(selectedRallyId),
  });

  const registrations = useMemo(() => {
    const list = normalizeRegistrations(registrationsData);
    const inspections = normalizeInspections(inspectionsData);
    return mergeRegistrationsWithInspections(list, inspections);
  }, [registrationsData, inspectionsData]);

  const {
    data: detailData,
    isLoading: isDetailLoading,
  } = useGetRegistrationById(detailId, {
    enabled: Boolean(detailId),
  });

  const detailRegistration = useMemo(() => {
    const detail = normalizeRegistration(detailData) || detailFallback;
    if (!detail) return null;
    const match = registrations.find(
      (item) => getEntityId(item) === getEntityId(detail)
    );
    return match ? { ...detail, ...match } : detail;
  }, [detailData, detailFallback, registrations]);

  const {
    mutate: updateApproval,
    isLoading: isApproving,
    isPending: isApprovePending,
  } = useUpdateCompetitorApprovalMutation();

  const {
    mutate: uploadConsent,
    isLoading: isUploadingConsent,
    isPending: isUploadPending,
  } = useUploadCategoryConsentMutation();

  const { mutateAsync: updateFiringOrder } = useUpdateFiringOrderMutation();
  const { mutateAsync: updateRallyNumber } = useUpdateRallyNumberMutation();

  const approving = isApproving || isApprovePending;
  const uploadingConsent = isUploadingConsent || isUploadPending;
  const uploadingCategoryId = uploadTarget
    ? getInheritedConsentInfo(uploadTarget, categories).categoryId
    : "";

  const contextLoading =
    isActiveRallyLoading || isPreviousLoading || isRalliesLoading;
  const selectedRally = rallyOptions.find(
    (item) => getEntityId(item) === selectedRallyId
  );

  const handleDownloadFile = (url) => {
    if (!url) return;
    openOrDownloadFile(url);
  };

  const handleDownloadConsent = async (registration) => {
    try {
      const result = await downloadPersonalizedConsent(
        registration,
        categories
      );
      if (!result.ok) {
        const message =
          result.reason === "no_driver_name"
            ? "Driver name is missing for this registration."
            : result.reason === "generate_failed"
              ? "Failed to generate consent PDF."
              : "No consent template for this category.";
        setFeedback({ type: "warning", message });
        return;
      }
      setFeedback({
        type: "success",
        message: "Personalized consent PDF downloaded.",
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: getRegistrationApiErrorMessage(
          error,
          "Failed to generate consent PDF."
        ),
      });
    }
  };

  const openDetail = (registration, mode = "view") => {
    const id = getEntityId(registration);
    if (!id) return;
    setDetailFallback(registration);
    setDetailId(id);
    setDetailMode(mode);
  };

  const closeDetail = () => {
    setDetailId("");
    setDetailFallback(null);
    setDetailMode("view");
  };

  const handleApproval = (registration, nextStatus) => {
    const registrationId = getEntityId(registration);
    if (!registrationId || !canManage) return;

    setFeedback({ type: "", message: "" });
    setApprovingId(registrationId);

    updateApproval(
      { registrationId, status: nextStatus },
      {
        onSuccess: () => {
          setFeedback({
            type: "success",
            message:
              nextStatus === "approved"
                ? "Registration approved."
                : "Registration set back to pending.",
          });
          setApprovingId("");
          if (detailId === registrationId) {
            setDetailFallback((prev) =>
              prev ? { ...prev, status: nextStatus } : prev
            );
          }
        },
        onError: (error) => {
          setFeedback({
            type: "danger",
            message: getRegistrationApiErrorMessage(
              error,
              "Failed to update registration approval."
            ),
          });
          setApprovingId("");
        },
      }
    );
  };

  const handleOpenUpload = (registration) => {
    setUploadError("");
    setUploadTarget(registration);
  };

  const handleCloseUpload = () => {
    if (uploadingConsent) return;
    setUploadTarget(null);
    setUploadError("");
  };

  const handleUploadConsent = ({ categoryId: uploadCategoryId, file }) => {
    if (!canManage || !uploadCategoryId || !file) return;

    setUploadError("");
    uploadConsent(
      { categoryId: uploadCategoryId, file },
      {
        onSuccess: () => {
          setFeedback({
            type: "success",
            message:
              "Signed consent uploaded. All registrations in this category now show as signed.",
          });
          setUploadTarget(null);
        },
        onError: (error) => {
          setUploadError(
            getRegistrationApiErrorMessage(
              error,
              "Failed to upload signed consent."
            )
          );
        },
      }
    );
  };

  const handleSaveFiringOrder = async (registration, value) => {
    const registrationId = getEntityId(registration);
    if (!registrationId || !canManage) return;

    if (value === null || value === undefined || Number.isNaN(value)) {
      setFieldErrors((prev) => ({
        ...prev,
        [registrationId]: {
          ...(prev[registrationId] || {}),
          firing_order: "Enter a positive integer.",
        },
      }));
      return;
    }

    const firingOrder = Number(value);
    if (!Number.isInteger(firingOrder) || firingOrder < 1) {
      setFieldErrors((prev) => ({
        ...prev,
        [registrationId]: {
          ...(prev[registrationId] || {}),
          firing_order: "Firing order must be a positive integer.",
        },
      }));
      return;
    }

    setSavingFiringOrderId(registrationId);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (next[registrationId]) {
        const row = { ...next[registrationId] };
        delete row.firing_order;
        if (Object.keys(row).length) next[registrationId] = row;
        else delete next[registrationId];
      }
      return next;
    });

    try {
      await updateFiringOrder({
        registrationId,
        firing_order: firingOrder,
      });
      setFeedback({ type: "success", message: "Firing order saved." });
      await refetchRegistrations();
    } catch (error) {
      setFieldErrors((prev) => ({
        ...prev,
        [registrationId]: {
          ...(prev[registrationId] || {}),
          firing_order: getRegistrationApiErrorMessage(
            error,
            "Failed to save firing order."
          ),
        },
      }));
    } finally {
      setSavingFiringOrderId("");
    }
  };

  const handleSaveRallyNumber = async (registration, value) => {
    const registrationId = getEntityId(registration);
    if (!registrationId || !canManage) return;

    const rallyNumber = String(value || "").trim();
    if (!rallyNumber) {
      setFieldErrors((prev) => ({
        ...prev,
        [registrationId]: {
          ...(prev[registrationId] || {}),
          rally_number: "Rally number is required.",
        },
      }));
      return;
    }

    setSavingRallyNumberId(registrationId);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (next[registrationId]) {
        const row = { ...next[registrationId] };
        delete row.rally_number;
        if (Object.keys(row).length) next[registrationId] = row;
        else delete next[registrationId];
      }
      return next;
    });

    try {
      await updateRallyNumber({
        registrationId,
        rally_number: rallyNumber,
      });
      setFeedback({ type: "success", message: "Rally number saved." });
      await refetchRegistrations();
    } catch (error) {
      setFieldErrors((prev) => ({
        ...prev,
        [registrationId]: {
          ...(prev[registrationId] || {}),
          rally_number: getRegistrationApiErrorMessage(
            error,
            "Failed to save rally number."
          ),
        },
      }));
    } finally {
      setSavingRallyNumberId("");
    }
  };

  const handleToggleSelect = (id) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(registrations.map((item) => getEntityId(item)).filter(Boolean));
  };

  const handleBulkDownload = async () => {
    const selected = registrations.filter((item) =>
      selectedIds.includes(getEntityId(item))
    );

    if (!selected.length) {
      setFeedback({
        type: "warning",
        message: "Select at least one registration to download consent forms.",
      });
      return;
    }

    try {
      const result = await downloadConsentFormsForRegistrations(
        selected,
        categories
      );

      const skipParts = [];
      if (result.skippedNoTemplate) {
        skipParts.push(`${result.skippedNoTemplate} no template`);
      }
      if (result.skippedNoDriver) {
        skipParts.push(`${result.skippedNoDriver} missing driver name`);
      }
      const otherSkipped =
        result.skipped - result.skippedNoTemplate - result.skippedNoDriver;
      if (otherSkipped > 0) {
        skipParts.push(`${otherSkipped} failed`);
      }

      setFeedback({
        type: result.downloaded ? "success" : "warning",
        message: `Downloaded ${result.downloaded} consent form${
          result.downloaded === 1 ? "" : "s"
        }${
          result.skipped
            ? `, skipped ${result.skipped} (${skipParts.join(", ")})`
            : ""
        }.`,
      });
    } catch (error) {
      setFeedback({
        type: "danger",
        message: getRegistrationApiErrorMessage(
          error,
          "Failed to download consent forms."
        ),
      });
    }
  };

  const handleRallyChange = (rallyId) => {
    setSelectedRallyId(rallyId);
    setCategoryId("");
    setStatus("");
    setSelectedIds([]);
    setFieldErrors({});
  };

  const handleInspectionSaved = async () => {
    setFeedback({
      type: "success",
      message: "Inspection saved. Qualification status will refresh.",
    });
    await Promise.all([refetchRegistrations(), refetchInspections()]);
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Competitors</h4>
        </div>

        <div className="card-body">
          {!canManage && (
            <Alert variant="warning" className="mb-3">
              Admin access is required to edit inspection, firing order, rally
              number, approval, or signed consent.
            </Alert>
          )}

          {feedback.message && (
            <Alert
              variant={feedback.type || "info"}
              className="mb-3"
              dismissible
              onClose={() => setFeedback({ type: "", message: "" })}
            >
              {feedback.message}
            </Alert>
          )}

          {contextLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : isActiveRallyError && isRalliesError ? (
            <Alert variant="danger" className="mb-0">
              Failed to load rallies.{" "}
              {getRegistrationApiErrorMessage(
                activeRallyError || ralliesError,
                "Please try again."
              )}
            </Alert>
          ) : (
            <>
              <CompetitorsFilters
                rallies={rallyOptions}
                isRalliesLoading={contextLoading}
                selectedRallyId={selectedRallyId}
                onRallyChange={handleRallyChange}
                activeRallyId={activeEventId}
                categories={categories}
                isCategoriesLoading={isCategoriesLoading}
                selectedCategoryId={categoryId}
                onCategoryChange={(value) => {
                  setCategoryId(value);
                  setSelectedIds([]);
                }}
                selectedStatus={status}
                onStatusChange={(value) => {
                  setStatus(value);
                  setSelectedIds([]);
                }}
              />

              {selectedRally ? (
                <p className="text-muted mb-3">
                  Showing competitors for{" "}
                  <strong>{selectedRally.name || selectedRallyId}</strong>
                  {selectedRally.isActive || selectedRallyId === activeEventId
                    ? " (active rally)"
                    : ""}
                  .
                </p>
              ) : null}

              {selectedRallyId && registrations.length > 0 ? (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    disabled={selectedIds.length === 0}
                    onClick={handleBulkDownload}
                  >
                    Download consent forms
                    {selectedIds.length ? ` (${selectedIds.length})` : ""}
                  </Button>
                </div>
              ) : null}

              {!selectedRallyId ? (
                <div className="text-center text-muted py-5">
                  <p className="mb-0">
                    {activeEventId
                      ? "Loading active rally..."
                      : "No active rally found. Select a rally from the dropdown."}
                  </p>
                </div>
              ) : isRegistrationsLoading ||
                isRegistrationsFetching ||
                isInspectionsLoading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : isRegistrationsError ? (
                <div className="text-danger py-4">
                  Failed to load registrations.{" "}
                  {getRegistrationApiErrorMessage(
                    registrationsError,
                    "Please try again."
                  )}
                </div>
              ) : registrations.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <p className="mb-0">No competitors found for this rally.</p>
                </div>
              ) : (
                <CompetitorsTable
                  registrations={registrations}
                  categories={categories}
                  canManage={canManage}
                  selectedIds={selectedIds}
                  approvingId={approving ? approvingId : ""}
                  uploadingCategoryId={
                    uploadingConsent ? uploadingCategoryId : ""
                  }
                  savingFiringOrderId={savingFiringOrderId}
                  savingRallyNumberId={savingRallyNumberId}
                  fieldErrors={fieldErrors}
                  onToggleSelect={handleToggleSelect}
                  onToggleSelectAll={handleToggleSelectAll}
                  onApprove={(item) => handleApproval(item, "approved")}
                  onUnapprove={(item) => handleApproval(item, "pending")}
                  onView={(item) => openDetail(item, "view")}
                  onEdit={(item) => openDetail(item, "edit")}
                  onOpenTeam={(teamId, registration) =>
                    setTeamTarget({
                      teamId,
                      fallback: registration?.team_id || registration?.team,
                    })
                  }
                  onOpenInspection={setInspectionTarget}
                  onSaveFiringOrder={handleSaveFiringOrder}
                  onSaveRallyNumber={handleSaveRallyNumber}
                  onDownloadConsent={handleDownloadConsent}
                  onDownloadSignedConsent={handleDownloadFile}
                  onUploadSignedConsent={handleOpenUpload}
                />
              )}
            </>
          )}
        </div>
      </div>

      <RegistrationDetailModal
        show={Boolean(detailId) && detailMode === "view"}
        registration={detailRegistration}
        categories={categories}
        isLoading={isDetailLoading}
        canManage={canManage}
        approving={approving && approvingId === detailId}
        uploading={
          uploadingConsent &&
          getInheritedConsentInfo(detailRegistration, categories).categoryId ===
            uploadingCategoryId
        }
        mode="view"
        onClose={closeDetail}
        onApprove={(item) => handleApproval(item, "approved")}
        onUnapprove={(item) => handleApproval(item, "pending")}
        onDownloadConsent={handleDownloadConsent}
        onDownloadSignedConsent={handleDownloadFile}
        onUploadSignedConsent={handleOpenUpload}
        onOpenInspection={(item) => {
          setInspectionTarget(item);
        }}
        onOpenEdit={(item) => openDetail(item, "edit")}
      />

      <CompetitorEditModal
        show={Boolean(detailId) && detailMode === "edit"}
        registration={detailRegistration}
        categories={categories}
        isLoading={isDetailLoading}
        canManage={canManage}
        approving={approving && approvingId === detailId}
        uploading={
          uploadingConsent &&
          getInheritedConsentInfo(detailRegistration, categories).categoryId ===
            uploadingCategoryId
        }
        savingFiringOrder={savingFiringOrderId === detailId}
        savingRallyNumber={savingRallyNumberId === detailId}
        fieldErrors={fieldErrors[detailId] || {}}
        onClose={closeDetail}
        onApprove={(item) => handleApproval(item, "approved")}
        onUnapprove={(item) => handleApproval(item, "pending")}
        onDownloadConsent={handleDownloadConsent}
        onDownloadSignedConsent={handleDownloadFile}
        onUploadSignedConsent={handleOpenUpload}
        onOpenInspection={(item) => {
          setInspectionTarget(item);
        }}
        onSaveFiringOrder={handleSaveFiringOrder}
        onSaveRallyNumber={handleSaveRallyNumber}
      />

      <UploadConsentModal
        show={Boolean(uploadTarget)}
        registration={uploadTarget}
        categories={categories}
        uploading={uploadingConsent}
        error={uploadError}
        onClose={handleCloseUpload}
        onSubmit={handleUploadConsent}
      />

      <InspectionModal
        show={Boolean(inspectionTarget)}
        registration={inspectionTarget}
        readOnly={!canManage}
        onClose={() => setInspectionTarget(null)}
        onSaved={handleInspectionSaved}
      />

      <TeamDetailModal
        show={Boolean(teamTarget?.teamId)}
        teamId={teamTarget?.teamId || ""}
        fallback={teamTarget?.fallback || null}
        onClose={() => setTeamTarget(null)}
      />
    </div>
  );
};

export default CompetitorsListPage;
